import { useLayoutEffect, useRef, useState, useCallback } from 'react'

const PRESCRIPTION_CONTENT_WIDTH = 794

/**
 * Scales the prescription preview (794px wide) to fill the available panel width,
 * preserving aspect ratio so nothing is cut off horizontally.
 *
 * CSS transform: scale() shrinks content visually but does NOT change its layout size —
 * the browser still reserves the original (natural) pixel height in the flow.
 * This means a 2-page prescription at scale 0.6 would look 1-page tall visually,
 * but occupy 2-page height in layout, causing the parent to clip the overflow.
 *
 * Fix: apply a negative margin-bottom equal to the layout "surplus"
 *   marginBottom = (scale - 1) * naturalHeight   [negative when scale < 1]
 * so that:
 *   effective layout height = naturalHeight + marginBottom
 *                           = naturalHeight * scale   (= visual height)
 * This makes scroll positions correspond 1:1 to visual positions, and lets the
 * outer div (flex:1, overflowY:auto) scroll correctly through all pages.
 */
export default function PrescriptionPreviewScaleWrapper({ children }) {
  const outerRef = useRef(null)
  const innerRef = useRef(null)
  const [scale, setScale] = useState(1)
  const [naturalHeight, setNaturalHeight] = useState(0)

  const recalculate = useCallback(() => {
    const outer = outerRef.current
    const inner = innerRef.current
    if (!outer) return
    const w = outer.offsetWidth
    if (!w) return
    const nextScale = Math.min(1, w / PRESCRIPTION_CONTENT_WIDTH)
    setScale(nextScale)
    if (inner) {
      const nh = inner.scrollHeight
      setNaturalHeight(nh)
    }
  }, [])

  useLayoutEffect(() => {
    recalculate()
    const obs = new ResizeObserver(recalculate)
    if (outerRef.current) obs.observe(outerRef.current)
    return () => obs.disconnect()
  }, [recalculate])

  // recalculate when children change (prescription updates)
  useLayoutEffect(() => {
    recalculate()
  })

  // Margin compensation: collapses the layout surplus so that
  // layout height == visual height, enabling correct scroll behaviour.
  const marginBottom = naturalHeight > 0 ? (scale - 1) * naturalHeight : 0

  return (
    <div
      ref={outerRef}
      style={{
        flex: 1,
        minHeight: 0,
        width: '100%',
        overflowX: 'hidden',
        overflowY: 'auto',
      }}
    >
      <div
        ref={innerRef}
        style={{
          width: PRESCRIPTION_CONTENT_WIDTH,
          transformOrigin: 'top left',
          transform: `scale(${scale})`,
          marginBottom,
        }}
      >
        {children}
      </div>
    </div>
  )
}
